import "./QuizLeaderboard.scss";

import { isError } from "util";

import { Avatar, Button, Flex, Table, Text, Tooltip } from "@fluentui/react-northstar";
import { getImageCardUrl } from "@teams/cards";
import { totalQuizPoints, totalUserQuizPoints } from "@teams/client-shared";
import { TeamsApplicationTypes, TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { useMemo, useState } from "react";

import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { PollyIcon } from "../../../../../_shared/components/PollyIcon";
import { surveyApi } from "../../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";
import { getPageContext } from "../../../../../_shared/utils/teamsHelpers";
import { getFreeUserResultsUnavailableText } from "../../../../../_shared/utils/userHelpers";

type QuizLeaderboardProps = {
  survey: SurveyDTO;
  responses: TeamsSurveyResponse[];
  showPostResultsAction?: boolean;
  hidePointTotal?: boolean;
  maxDisplay?: number;
  showBackground?: boolean;
  onSelect?: (responseId: string) => void;
};

export const QuizLeaderboard: React.FC<QuizLeaderboardProps> = ({
  survey,
  responses,
  showPostResultsAction,
  hidePointTotal,
  maxDisplay,
  onSelect,
}) => {
  const { user } = useUserInfo();
  const { teamsContext } = useTeamsContext();
  const [isPostingNotification, setIsPostingNotification] = useState<boolean>();

  const applicationType = getPageContext()?.replace("/", "") as TeamsApplicationTypes;

  const totalPoints = useMemo(
    () => totalQuizPoints(survey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey.id]
  );

  const users = useMemo(
    () => {
      if (!responses?.length) {
        return [];
      }
      const responseDisplays = responses.map(response => {
        return {
          responseId: response.id,
          user: response.user,
          points: totalUserQuizPoints(survey.questions, response),
        };
      });

      responseDisplays.sort((a, b) => b.points - a.points);

      if (maxDisplay) {
        responseDisplays.splice(maxDisplay);
      }

      return responseDisplays;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey.id, responses]
  );

  const hiddenResults = survey.responseCount && survey.responseCount > 0 && !responses;

  return (
    <Flex column gap="gap.medium" className="quiz-leader-board">
      {!hidePointTotal && (
        <Text
          content={`Total quiz points available: ${totalPoints}`}
          className="quiz-leader-board__sub-title"
        />
      )}

      {!!hiddenResults && (
        <Flex
          column
          hAlign="center"
          vAlign="center"
          gap="gap.medium"
          className="quiz-leader-board__resultsUnavailable"
        >
          <Text size="medium" weight="semibold" color="brand" align="center">
            {getFreeUserResultsUnavailableText(user)}
          </Text>
          <img
            src={getImageCardUrl("RESULTS_UNAVAILABLE", applicationType)}
            width="186"
            height="129"
            alt="Results unavailable"
          />
        </Flex>
      )}

      {!hiddenResults && (
        <Table
          compact
          header={{
            as: "div",
            className: "quiz-leader-board__header-row",
            key: "header-row",
            items: [
              {
                className: "quiz-leader-board__header-row__user-name",
                content: (
                  <Text
                    content="User Name"
                    key="users-header"
                    className="quiz-leader-board__sub-title"
                  />
                ),
              },
              {
                className: "quiz-leader-board__header-row__result",
                content: (
                  <Text
                    content="Result"
                    key="results-header"
                    className="quiz-leader-board__sub-title"
                  />
                ),
              },
            ],
          }}
          rows={users.map(row => {
            const rowId = row.responseId;
            return {
              as: "div",
              className: "quiz-leader-board__result-row",
              key: rowId,
              items: [
                {
                  className: "quiz-leader-board__result-row__user-name",
                  content: (
                    <Flex fill gap="gap.small" vAlign="center" key={`${rowId}-rankings-name-cell`}>
                      <Avatar
                        name={row.user.name}
                        size="small"
                        className="quiz-leader-board__avatar"
                      />
                      <Text
                        className={`quiz-leader-board__user-name ${
                          onSelect ? "quiz-leader-board__link" : undefined
                        }`}
                        content={row.user.name}
                        onClick={onSelect ? () => onSelect(row.responseId) : undefined}
                      />
                    </Flex>
                  ),
                },
                {
                  className: "quiz-leader-board__result-row__result",
                  content: <Flex key={`${rowId}-rankings-count-cell`}>{`${row.points}`}</Flex>,
                },
              ],
            };
          })}
        />
      )}

      {maxDisplay !== undefined && responses?.length > maxDisplay && (
        <div className="quiz-leader-board__more-responses">
          <Text size="small" weight="bold" content={`+${responses.length - maxDisplay} more`} />
        </div>
      )}

      {showPostResultsAction &&
        survey.role === "own" &&
        survey.state === "closed" &&
        survey.resultsVisibility !== "private" && (
          <Tooltip content="Post a notification to all members displaying the results">
            <div>
              <Button
                primary
                fluid
                content="Post results"
                disabled={isPostingNotification}
                loading={isPostingNotification}
                icon={<PollyIcon icon="notification-icon" inheritColor />}
                onClick={async () => {
                  setIsPostingNotification(true);
                  const result = await surveyApi.notifyAudienceResults(
                    teamsContext,
                    survey.id as string,
                    "quiz-leaderboard"
                  );

                  if (isError(result)) {
                    console.error(result);
                  }

                  setIsPostingNotification(false);
                }}
              />
            </div>
          </Tooltip>
        )}
    </Flex>
  );
};
